import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { ACTIVITY_BAR_WIDTH_PX, COLLAPSED_RAIL_WIDTH_PX, SASH_WIDTH_PX } from "./panelSizing";

export function WorkbenchLayout({
  showActivityBar,
  activityBar,
  visibleLeft,
  visibleRight,
  hideSidePanels,
  focusMode,
  leftPanelWidth,
  rightPanelWidth,
  resizingPanel,
  leftAriaValueNow,
  rightAriaValueNow,
  onResizeMouseDown,
  onResizeDoubleClick,
  leftAside,
  center,
  rightAside,
}: {
  showActivityBar: boolean;
  activityBar: ReactNode;
  visibleLeft: boolean;
  visibleRight: boolean;
  /** When true, omit chapter/assistant columns entirely (Formatter / Settings). */
  hideSidePanels?: boolean;
  focusMode: boolean;
  leftPanelWidth: number;
  rightPanelWidth: number;
  resizingPanel: "left" | "right" | null;
  leftAriaValueNow: number;
  rightAriaValueNow: number;
  onResizeMouseDown: (side: "left" | "right", clientX: number) => void;
  onResizeDoubleClick: (side: "left" | "right") => void;
  leftAside: ReactNode;
  center: ReactNode;
  rightAside: ReactNode;
}) {
  const collapsedRailWidth = COLLAPSED_RAIL_WIDTH_PX;
  const activityCol = showActivityBar && !focusMode ? `${ACTIVITY_BAR_WIDTH_PX}px` : "0px";

  const leftCol = hideSidePanels ? "0px" : visibleLeft ? `${leftPanelWidth}px` : `${collapsedRailWidth}px`;
  const leftSash = hideSidePanels || !visibleLeft ? "0px" : `${SASH_WIDTH_PX}px`;
  const rightCol = hideSidePanels ? "0px" : visibleRight ? `${rightPanelWidth}px` : `${collapsedRailWidth}px`;
  const rightSash = hideSidePanels || !visibleRight ? "0px" : `${SASH_WIDTH_PX}px`;

  const mainTemplate = `${activityCol} ${leftCol} ${leftSash} minmax(0, 1fr) ${rightSash} ${rightCol}`;

  return (
    <main
      className="grid min-h-0 flex-1 gap-0 overflow-hidden"
      style={{ gridTemplateColumns: mainTemplate }}
    >
      {showActivityBar && !focusMode ? (
        <div className="box-border flex min-w-0 w-full max-w-full flex-col overflow-hidden border-r border-border bg-(--workbench-activityBar)">
          {activityBar}
        </div>
      ) : (
        <div className="m-0 w-0 overflow-hidden border-0 p-0" aria-hidden />
      )}

      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        {hideSidePanels ? null : leftAside}
      </div>

      {!hideSidePanels && visibleLeft ? (
        <div
          className={cn(
            "w-1.5 cursor-col-resize rounded-lg bg-border/60 hover:bg-primary/45 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2",
            resizingPanel === "left" && "bg-primary/45"
          )}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chapters panel"
          aria-valuemin={220}
          aria-valuemax={480}
          aria-valuenow={Math.round(leftAriaValueNow)}
          tabIndex={0}
          onMouseDown={(event) => onResizeMouseDown("left", event.clientX)}
          onDoubleClick={() => onResizeDoubleClick("left")}
        />
      ) : (
        <div className="min-w-0 w-0" />
      )}

      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-x border-border bg-(--workbench-editor)">
        {center}
      </div>

      {!hideSidePanels && visibleRight ? (
        <div
          className={cn(
            "w-1.5 cursor-col-resize rounded-lg bg-border/60 hover:bg-primary/45 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2",
            resizingPanel === "right" && "bg-primary/45"
          )}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize assistant panel"
          aria-valuemin={280}
          aria-valuemax={620}
          aria-valuenow={Math.round(rightAriaValueNow)}
          tabIndex={0}
          onMouseDown={(event) => onResizeMouseDown("right", event.clientX)}
          onDoubleClick={() => onResizeDoubleClick("right")}
        />
      ) : (
        <div className="min-w-0 w-0" />
      )}

      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        {hideSidePanels ? null : rightAside}
      </div>
    </main>
  );
}
